import { createAsyncThunk } from '@reduxjs/toolkit';
import { addDays } from 'date-fns';
import { sortBy } from 'lodash';
import { fetchAllRoutes, fetchSailings, fetchVehicleTypeOptionsCached } from '../../../common/api';
import { assertIsDefined } from '../../../common/asserts/assert';
import type { GoodsItem } from '../../../common/goods/goodsUtils';
import type { Route } from '../../../common/graphql/fragments/gql/Route';
import type { SettingsFragment } from '../../../common/graphql/fragments/gql/SettingsFragment';
import type { VehicleType } from '../../../common/graphql/fragments/gql/VehicleType';
import { getFirstValidCustomer, getValidCustomer } from '../../../common/utils/customerUtils';
import { formatServerDate } from '../../../common/utils/dates/dateUtils';
import { getSingleBookableSailing } from '../../../common/utils/sailingUtils';
import { DateSetting } from '../../../gql/graphql';
import type { StoreState } from '../../../store/storeState';
import { getInitialCreateCandidate } from '../../../use-cases/create/utils/bookingCandidateUtils';
import { getAccount } from '../../settings/settingsReducer';
import {
  type CreateFormIdVariant,
  initializeCreateForm as initializeCreateFormAction,
  requestCleanupCreateForm,
} from '../createFormActions';

async function getRouteFromSettings(accountSettings: SettingsFragment): Promise<Route | null> {
  if (accountSettings.bookDefaultRoute) {
    return accountSettings.bookDefaultRoute;
  } else {
    return accountSettings.favouriteRoutes.find(r => !r.isClosed) ?? null;
  }
}

async function getRoute(accountSettings: SettingsFragment): Promise<Route | null> {
  const settingsRoute = await getRouteFromSettings(accountSettings);

  if (settingsRoute) {
    return !settingsRoute.isClosed ? settingsRoute : null;
  }

  const routes = await fetchAllRoutes();

  return routes?.[0] ?? null;
}

const getVehicleTypeFromSettings = (accountSettings: SettingsFragment): VehicleType | null => {
  if (accountSettings.bookDefaultVehicleType) {
    return accountSettings.bookDefaultVehicleType;
  } else if (accountSettings.favouriteVehicleTypes.length > 0) {
    return accountSettings.favouriteVehicleTypes[0];
  } else {
    return null;
  }
};

async function getVehicleType(
  accountSettings: SettingsFragment,
  customerNo: number,
  routeCode: string,
): Promise<VehicleType | null> {
  const vehicleType = getVehicleTypeFromSettings(accountSettings);
  if (vehicleType) {
    return vehicleType;
  }

  const vehicleTypes = sortBy(await fetchVehicleTypeOptionsCached(customerNo, routeCode), [
    'description',
  ]);
  return vehicleTypes?.[0] ?? null;
}

export const initializeCreateFormThunk = createAsyncThunk<
  void,
  { formId: CreateFormIdVariant; initialGoodsItem?: GoodsItem },
  { state: StoreState }
>('initializeCreateForm', async ({ formId, initialGoodsItem }, { dispatch, getState }) => {
  dispatch(requestCleanupCreateForm(formId));

  const today = new Date();
  const tomorrow = addDays(today, 1);

  const account = getAccount(getState());

  assertIsDefined(account, 'No account found to initialize create form');

  const { accountSettings, customers } = account;

  const departureDate = formatServerDate(
    accountSettings.bookDate === DateSetting.Today ? today : tomorrow,
  );

  const customer =
    getValidCustomer(accountSettings.bookCustomer) ?? getFirstValidCustomer(customers);

  const route = await getRoute(accountSettings);

  const [vehicleType, sailings] = await Promise.all([
    customer != null && route != null
      ? getVehicleType(accountSettings, customer.custNo, route.code)
      : null,
    route != null ? fetchSailings(route.code, departureDate) : [],
  ]);

  const candidate = getInitialCreateCandidate(
    vehicleType,
    vehicleType?.defaultHeight ?? 0,
    vehicleType?.defaultLength ?? 0,
    vehicleType?.defaultWidth ?? 0,
    vehicleType?.defaultTradeWeight ?? 0,
    initialGoodsItem,
  );

  dispatch(
    initializeCreateFormAction(formId, {
      bookings: [candidate],
      initialBookings: [candidate],
      customer,
      departureDate,
      route,
      sailing: getSingleBookableSailing(sailings),
    }),
  );
});
